// src/lib/school/access-after-expiry.ts
//
// What a school student is told about the day their school's access ends.
//
// The dashboard, /pricing and /settings all named the last day ("full access
// through Oct 14") and nothing said what happens on the 15th (external QA). This
// is that answer, in one place, read from the code that decides it:
//
//   KEPT. Nothing is deleted when a license ends. getEffectivePlan simply stops
//   returning PRO for the school (effective-plan.ts, step 0) and the account
//   falls back to whatever else it has — usually Free. Attempts, practice
//   history, the notebook and review cards are rows on the user, untouched.
//   The school's own records stay the school's: it can export them or ask us to
//   delete them (cron/school-license-expiry tells school admins exactly that),
//   so "nothing is deleted" carries that one exception.
//
//   CHANGED. Free's limits, from plan-config / plan-copy — never typed out here,
//   so a change to the allowances changes this copy too. Pro-only features come
//   from FEATURE_REQUIREMENTS.
//
//   TEACHERS. Membership is kept — the student stays on the class roster — but
//   every staff read is limited to work the license covered
//   (license-visibility.ts, decided 2026-10): after the last day, teachers and
//   school admins see work up to it and nothing done afterwards. A renewal
//   shares work again from the day it starts, never work done in between.
//   Those filters are what make this line true; change one, change both.
//
//   CONTINUING. A renewal brings Pro back automatically (the license dates are
//   the entitlement). Or the student can buy Pro once the school's access has
//   ended — checkout is refused while it runs (student-mode.ts SCHOOL_COVERED).
//
// Pure, so client components (SchoolStudentNotice) can use it.

import { getEffectivePlan, type EffectivePlanInput } from '@/lib/billing/effective-plan'
import { FEATURE_REQUIREMENTS, PLANS, type FeatureKey } from '@/lib/billing/plan-config'
import { FREE_TEST_ALLOWANCES, sageAllowance } from '@/lib/billing/plan-copy'
import { SCHOOL_TIME_ZONE, licenseLastDate, licenseLastDayLabel } from './license-dates'
import { userDateString } from '@/lib/time/user-day'

/** Days before the last day that the pre-expiry warning starts (mirrors the admin email). */
export const SCHOOL_EXPIRY_NOTICE_DAYS = 14

// The Pro-only features a student would notice losing, named in words. Only
// those FEATURE_REQUIREMENTS actually puts on Pro are listed.
const PRO_FEATURE_WORDS: Partial<Record<FeatureKey, string>> = {
  studyPlan: 'your personalized study plan',
  detailedAnalytics: 'full analytics',
  mistakeDna: 'Mistake DNA',
  timeAnalytics: 'pacing analytics',
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** The Pro-only features, e.g. "your personalized study plan, full analytics and Mistake DNA". */
export function proOnlyFeaturePhrase(): string {
  const words = (Object.keys(PRO_FEATURE_WORDS) as FeatureKey[])
    .filter((k) => FEATURE_REQUIREMENTS[k] === 'PRO')
    .map((k) => PRO_FEATURE_WORDS[k] as string)
  return joinList(words)
}

export type SchoolAfterAccess = {
  heading: string
  keeps: string
  changes: string
  teachers: string
  continues: string
}

/**
 * The "what happens after {last day}" explanation.
 *
 * `keepsPro`: the student has Pro of their own that outlasts the license (a
 * subscription, a comp, the App Store) — then nothing changes, and saying it
 * moves them to Free would be wrong.
 */
export function schoolAccessAfterCopy({
  schoolName,
  lastDayLabel,
  keepsPro = false,
}: {
  schoolName: string
  lastDayLabel: string
  keepsPro?: boolean
}): SchoolAfterAccess {
  const pro = proOnlyFeaturePhrase()
  return {
    heading: `What happens after ${lastDayLabel}`,
    keeps:
      'Your account and everything you have done stay saved: test scores and history, practice ' +
      `progress, your notebook and review cards. Nothing is deleted when ${schoolName}'s access ` +
      'ends (only if the school asks us to delete its records).',
    changes: keepsPro
      ? 'You keep Pro through your own plan, so nothing changes for you.'
      : `Your account moves to the Free plan: unlimited topic practice, a daily ` +
        `${PLANS.FREE.drillQuestionsPerDay}-question drill, ${FREE_TEST_ALLOWANCES}, and ` +
        `${sageAllowance('FREE')}.${pro ? ` Pro features such as ${pro} lock.` : ''}`,
    teachers:
      `You stay in your classes, but your teachers and school admins see only the work you did ` +
      `through ${lastDayLabel}. Anything you do after that stays private to you. If ` +
      `${schoolName} renews, they see your work again from the day it renews — never what you ` +
      'did in between.',
    continues: keepsPro
      ? `If ${schoolName} renews, its access simply picks up again.`
      : `If ${schoolName} renews, full access comes back automatically — there is nothing for ` +
        'you to do. Otherwise you can upgrade to Pro yourself once its access has ended.',
  }
}

/**
 * Whether the student still has Pro the moment the license ends, from anything
 * but the school: the effective plan at that instant with the school removed.
 */
export function keepsProAfterSchool(user: EffectivePlanInput, expiresAt: Date): boolean {
  return getEffectivePlan({ ...user, orgMemberships: [] }, expiresAt.getTime()) !== 'FREE'
}

/**
 * Whole calendar days from today to the last covered day, both as YYYY-MM-DD
 * in school time (license-dates.ts). 0 = today is the last day.
 */
export function daysUntilLastDay(todayYmd: string, lastDayYmd: string): number {
  const [fy, fm, fd] = todayYmd.split('-').map(Number)
  const [ty, tm, td] = lastDayYmd.split('-').map(Number)
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000)
}

/**
 * The pre-expiry warning line, worded by the LAST DAY so the count can't be
 * read off by one: "Your access from Lincoln High ends soon: Oct 14, 2026 is
 * the last day (in 5 days)."
 */
export function schoolExpiryNoticeMessage(opts: {
  schoolName: string
  daysLeft: number
  lastDayLabel: string
}): string {
  const lead = `Your access from ${opts.schoolName}`
  if (opts.daysLeft <= 0) return `${lead} ends soon: today, ${opts.lastDayLabel}, is the last day.`
  if (opts.daysLeft === 1)
    return `${lead} ends soon: tomorrow, ${opts.lastDayLabel}, is the last day.`
  return `${lead} ends soon: ${opts.lastDayLabel} is the last day (in ${opts.daysLeft} days).`
}

export type SchoolExpiryNoticeState = {
  schoolName: string
  /** Calendar days (school time) from today to the last day; 0 = today is the last day. */
  daysLeft: number
  lastDayLabel: string
  /** Today or tomorrow is the last day. */
  urgent: boolean
}

export type SchoolExpiryInput = Omit<EffectivePlanInput, 'orgMemberships'> & {
  orgMemberships: Array<{
    role?: string
    org: { name?: string; startsAt: Date; expiresAt: Date | null }
  }>
}

/**
 * The pre-expiry warning for a school student, or null — mirrors
 * compExpiryNoticeState (comp-expiry-notice.ts). Silent when:
 *   - they are not a STUDENT of a school whose license is running now;
 *   - the last day is SCHOOL_EXPIRY_NOTICE_DAYS or more away;
 *   - they keep Pro anyway (their own subscription, comp or App Store outlasts
 *     the license), so warning them about losing access would be untrue.
 */
export function schoolExpiryNoticeState(
  user: SchoolExpiryInput,
  now: Date = new Date(),
): SchoolExpiryNoticeState | null {
  const nowMs = now.getTime()
  const m = (user.orgMemberships ?? []).find(
    ({ role, org }) =>
      (role === undefined || role === 'STUDENT') &&
      org.expiresAt !== null &&
      org.startsAt.getTime() <= nowMs &&
      nowMs < org.expiresAt.getTime(),
  )
  if (!m || !m.org.expiresAt) return null
  const daysLeft = daysUntilLastDay(
    userDateString(SCHOOL_TIME_ZONE, now),
    licenseLastDate(m.org.expiresAt),
  )
  if (daysLeft < 0 || daysLeft >= SCHOOL_EXPIRY_NOTICE_DAYS) return null
  if (keepsProAfterSchool(user, m.org.expiresAt)) return null
  return {
    schoolName: m.org.name ?? 'Your school',
    daysLeft,
    lastDayLabel: licenseLastDayLabel(m.org.expiresAt),
    urgent: daysLeft <= 1,
  }
}
